/**
 * @file Frontend API client — calorificValuesAdminService.
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";

/** Type definition — Calorific Value Admin. */
export interface CalorificValueAdmin {
  id: number;
  fuelMix: string;
  unit: string;
  calorificValue: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Type definition — Calorific Value Create Input. */
export interface CalorificValueCreateInput {
  fuelMix: string;
  unit: string;
  calorificValue?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

/** Type definition — Calorific Value Update Input. */
export type CalorificValueUpdateInput = Partial<CalorificValueCreateInput>;

function apiMessage(err: unknown): string {
  const e = err as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    "Request failed"
  );
}

/** Exported const — calorific Values Admin Service. */
export const calorificValuesAdminService = {
  async list(): Promise<CalorificValueAdmin[]> {
    try {
      const res = await axiosClient.get<{ success?: boolean; data?: CalorificValueAdmin[] }>(
        API_URLS.ADMIN.CALORIFIC_VALUES.LIST
      );
      if (res.data?.success === false) {
        throw new Error((res.data as { message?: string }).message || "Failed to list calorific values");
      }
      const rows = res.data?.data;
      return Array.isArray(rows) ? rows : [];
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async getOne(id: number): Promise<CalorificValueAdmin> {
    try {
      const res = await axiosClient.get<{ success?: boolean; data?: CalorificValueAdmin }>(
        API_URLS.ADMIN.CALORIFIC_VALUES.BY_ID(id)
      );
      if (res.data?.success === false || !res.data?.data) {
        throw new Error((res.data as { message?: string })?.message || "Not found");
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async create(body: CalorificValueCreateInput): Promise<CalorificValueAdmin> {
    try {
      const res = await axiosClient.post<{ success?: boolean; data?: CalorificValueAdmin }>(
        API_URLS.ADMIN.CALORIFIC_VALUES.LIST,
        body
      );
      if (res.data?.success === false || !res.data?.data) {
        throw new Error((res.data as { message?: string })?.message || "Create failed");
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async update(id: number, body: CalorificValueUpdateInput): Promise<CalorificValueAdmin> {
    try {
      const res = await axiosClient.put<{ success?: boolean; data?: CalorificValueAdmin }>(
        API_URLS.ADMIN.CALORIFIC_VALUES.BY_ID(id),
        body
      );
      if (res.data?.success === false || !res.data?.data) {
        throw new Error((res.data as { message?: string })?.message || "Update failed");
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async delete(id: number): Promise<void> {
    try {
      const res = await axiosClient.delete<{ success?: boolean; message?: string }>(
        API_URLS.ADMIN.CALORIFIC_VALUES.BY_ID(id)
      );
      if (res.data?.success === false) {
        throw new Error(res.data.message || "Delete failed");
      }
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },
};
